import api, { formatApiError } from "./api";

// Module-level state survives Layout unmounts when navigating between pages.
let running = false;
const listeners = new Set();
export const isDatabaseBackupRunning = () => running;
export function subscribeDatabaseBackup(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
function setRunning(value) {
    running = value;
    listeners.forEach((listener) => listener());
}

export function backupFilename(disposition) {
    const fallback = `emprenovus-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.zip`;
    const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
    const plain = disposition?.match(/filename="([^"]+)"|filename=([^;]+)/i);
    let name;
    try {
        name = encoded ? decodeURIComponent(encoded[1]) : plain?.[1] || plain?.[2];
    } catch {
        return fallback;
    }
    // Only use a simple ZIP filename, never a server-supplied path.
    return name && /^[a-zA-Z0-9_.-]+\.zip$/i.test(name.trim()) ? name.trim() : fallback;
}

export async function backupErrorMessage(error) {
    let data = error?.response?.data;
    // Axios receives HTTP errors as blobs too when responseType is 'blob'.
    if (data instanceof Blob) {
        try { data = JSON.parse(await data.text()); } catch { data = null; }
    }
    const status = error?.response?.status;
    if (data?.detail && status !== 401 && !(status === 403 && data.detail === "Forbidden")) return formatApiError(data.detail);
    switch (status) {
        case 401: return "Tu sesi\u00f3n expir\u00f3. Inicia sesi\u00f3n nuevamente para descargar el respaldo.";
        case 403: return "Solo un superadministrador activo puede descargar la base completa.";
        case 409: return "Ya hay un respaldo en curso. Espera a que termine.";
        case 502:
        case 504: return "El servidor interrumpi\u00f3 la descarga. No se descarg\u00f3 un respaldo completo; revisa el tiempo l\u00edmite del servidor.";
        default: return "No se pudo descargar el respaldo completo. Revisa tu conexi\u00f3n e intenta nuevamente.";
    }
}

export async function downloadDatabaseBackup() {
    if (running) throw new Error("Ya hay un respaldo en curso.");
    setRunning(true);
    let objectUrl;
    let anchor;
    try {
        const response = await api.post("/admin/database-backup", null, {
            responseType: "blob",
            // The backend has its own configurable deadline. A short Axios timeout
            // would abort a valid export just because the database has grown.
            timeout: 0,
            headers: { Accept: "application/zip" },
        });
        const blob = response.data;
        if (!(blob instanceof Blob) || blob.size < 22) {
            throw new Error("El servidor devolvi\u00f3 un respaldo vac\u00edo o inv\u00e1lido.");
        }
        // Do not save a proxy/login HTML page (HTTP 200) as a fake backup ZIP.
        const signature = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
        const trailer = new Uint8Array(await blob.slice(-22).arrayBuffer());
        if (signature[0] !== 0x50 || signature[1] !== 0x4b || signature[2] !== 0x03 || signature[3] !== 0x04
            || trailer[0] !== 0x50 || trailer[1] !== 0x4b || trailer[2] !== 0x05 || trailer[3] !== 0x06) {
            throw new Error("La respuesta no es un respaldo ZIP v\u00e1lido. No se guard\u00f3 ning\u00fan archivo.");
        }
        const expectedHash = response.headers["x-backup-sha256"];
        if (expectedHash && window.crypto?.subtle) {
            const digest = await window.crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
            const actualHash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
            if (actualHash !== expectedHash.toLowerCase()) {
                throw new Error("La descarga no supera la verificaci\u00f3n de integridad. No se guard\u00f3 el archivo.");
            }
        }
        const filename = backupFilename(response.headers["content-disposition"]);
        objectUrl = URL.createObjectURL(blob);
        anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = filename;
        anchor.style.display = "none";
        document.body.appendChild(anchor);
        anchor.click();
        return { filename, consistency: response.headers["x-backup-consistency"] };
    } catch (error) {
        if (error?.isAxiosError || error?.response) {
            throw new Error(await backupErrorMessage(error));
        }
        throw error;
    } finally {
        anchor?.remove();
        // Delayed revocation gives Safari enough time to consume the object URL.
        if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        setRunning(false);
    }
}
